import clsx from 'clsx'
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import { CSVLink } from 'react-csv'
import type { Data as CsvData } from 'react-csv/lib/core'
import { useSearchParams } from 'react-router-dom'

import { Button, Tabs } from '../../designs'
import { useEnhancedSearchParams } from '../../hooks'
import { SubSearchParamEnum } from '../../types/searchParams'
import { VariableCombinationBarChart } from '../Charts'
import type { VariabeCombinationDataEntry } from '../Charts/VariableCombinationBarChart'
import { VisualizationContainer } from '../containers'
import { DataDisplayTable } from '../tables'
import {
  convertCombinationChartChartDataToVisualizationValues,
  convertVariableCombinationChartDataToTableBody,
  variableCombinationChartDataToTableHead,
} from '../tables/dataUtils'

interface Props {
  label: string
  data: VariabeCombinationDataEntry[]
  unit: string
  numberRounding?: number
  description?: string
  toggleActions?: ReactElement[]
  selectActions?: ReactElement[]
  otherActions?: ReactElement[]
}

enum VisualizationModeEnum {
  CHART = 'chart',
  TABLE = 'table',
}

const visualizationOtions = [
  { label: 'Chart', value: VisualizationModeEnum.CHART },
  { label: 'Table', value: VisualizationModeEnum.TABLE },
]

const VariableCombinationBarChartAndTableDisplay = ({
  label,
  data,
  unit,
  description,
  numberRounding = 10,
  toggleActions = [],
  selectActions = [],
  otherActions = [],
}: Props) => {
  const { updateSearchParam } = useEnhancedSearchParams()
  const [searchParams] = useSearchParams()
  const [visualizationTabValue, setVisualizationTabValue] = useState<VisualizationModeEnum>(
    visualizationOtions[0].value,
  )
  const [csvData, setCsvData] = useState<CsvData | null>(null)
  const [visibleData, setVisibleData] = useState<number>(50)

  // Update state when URL search params change.
  useEffect(() => {
    const mode = searchParams.get(SubSearchParamEnum.VISUALIZATION_MODE) as VisualizationModeEnum
    setVisualizationTabValue(
      mode === VisualizationModeEnum.TABLE
        ? visualizationOtions[1].value
        : visualizationOtions[0].value,
    )
  }, [searchParams, setVisualizationTabValue])

  // Update URL search params when visualization mode is changed.
  const onChangeVisualization = useCallback(
    (value: VisualizationModeEnum) =>
      updateSearchParam(SubSearchParamEnum.VISUALIZATION_MODE, value),
    [updateSearchParam],
  )

  const csvExportButton = useMemo(() => {
    return csvData && visualizationTabValue === visualizationOtions[1].value
      ? [
          <CSVLink
            key='csv-export'
            data={csvData}
            filename={'pi-export.csv'}
            className={clsx(
              'inline-flex items-center border font-medium focus:outline-none focus:ring-1',
              'rounded-md border-gray-300 bg-white px-2.5 py-1 text-sm shadow-sm focus:ring-offset-2',
              'text-gray-700 hover:bg-gray-100 focus:border-primary-500 focus:ring-primary-500  disabled:cursor-not-allowed',
              'disabled:border-gray-200 disabled:bg-gray-50 disabled:text-gray-500',
            )}
            target='_blank'
          >
            Export to CSV
          </CSVLink>,
        ]
      : []
  }, [csvData, visualizationTabValue])

  const tableHeaderValues = useMemo(
    () => [...variableCombinationChartDataToTableHead(data), unit],
    [data, unit],
  )
  const tableBody = useMemo(
    () => convertVariableCombinationChartDataToTableBody(data, numberRounding),
    [data, numberRounding],
  )
  const tableVisualizationValues = useMemo(
    () => convertCombinationChartChartDataToVisualizationValues(data),
    [data],
  )

  const setCsvExportData = useCallback((data: CsvData) => setCsvData(data), [setCsvData])

  return (
    <VisualizationContainer
      label={label}
      description={description}
      toggleActions={toggleActions}
      selectActions={selectActions}
      otherActions={[
        ...otherActions,
        <Tabs
          key='select-visualization'
          variant='secondary'
          size='xs'
          options={visualizationOtions}
          value={visualizationTabValue}
          onChange={onChangeVisualization as (value: string) => void}
        />,
        ...csvExportButton,
      ]}
    >
      {visualizationTabValue === visualizationOtions[0].value ? (
        <>
          <VariableCombinationBarChart data={data.slice(0, visibleData)} unit={unit} />
          <Button onClick={() => setVisibleData((curValue) => curValue + 50)}>Show more</Button>
        </>
      ) : (
        <DataDisplayTable
          head={tableHeaderValues}
          body={tableBody}
          visualizationValues={tableVisualizationValues}
          setExportedData={setCsvExportData}
        />
      )}
    </VisualizationContainer>
  )
}

export default VariableCombinationBarChartAndTableDisplay
